import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '../atoms/Button';
import { LanguageSwitcher } from '../molecules';
import { useTranslation } from 'react-i18next';

const Header = ({ onContactClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname.match(/^\/(ja|en|th)\/?$/);
  const { t } = useTranslation();

  const MenuIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  );

  const CloseIcon = () => (
    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  // ナビゲーション項目の設定
  const navigationItems = [
    {
      id: 'services',
      label: t('navigation.services'),
      // ホームページの場合はスクロール、その他はホームページの該当セクションに遷移
      onClick: isHomePage
        ? (e) => {
          e.preventDefault();
          document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
        }
        : null,
      to: isHomePage ? '#services' : '/#services'
    },
    {
      id: 'about',
      label: t('navigation.about'),
      isExternal: true,
      href: 'https://irori-japan.studio.site/',
      onClick: (e) => {
        console.log('IRORIについて clicked'); // デバッグ用
        e.preventDefault();
        e.stopPropagation();
        window.open('https://irori-japan.studio.site/', '_blank', 'noopener,noreferrer');
      }
    }
  ];

  const handleNavClick = (item) => {
    if (item.onClick) {
      item.onClick();
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">Icon</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-orange-700">IRORI</h1>
              <p className="text-sm text-gray-600">TOURISM</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigationItems.map((item) => (
              item.isExternal ? (
                <a
                  key={item.id}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium hover:text-orange-500 transition-colors cursor-pointer"
                  onClick={(e) => {
                    console.log('External link clicked:', item.href);
                  }}
                >
                  {item.label}
                </a>
              ) : item.onClick ? (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  className="text-sm font-medium hover:text-orange-500 transition-colors cursor-pointer"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.id}
                  to={item.to}
                  className="text-sm font-medium hover:text-orange-500 transition-colors"
                >
                  {item.label}
                </Link>
              )
            ))}
            <Button size="small" onClick={onContactClick}>
              {t('common.contact')}
            </Button>
            <LanguageSwitcher />
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 hover:text-orange-500 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-200">
            <nav className="flex flex-col space-y-4 pt-4">
              {navigationItems.map((item) => (
                item.isExternal ? (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-sm font-medium hover:text-orange-500 transition-colors cursor-pointer py-2 text-left block"
                  >
                    {item.label}
                  </a>
                ) : item.onClick ? (
                  <button
                    key={item.id}
                    onClick={() => handleNavClick(item)}
                    className="text-sm font-medium hover:text-orange-500 transition-colors cursor-pointer py-2 text-left"
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.id}
                    to={item.to}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-sm font-medium hover:text-orange-500 transition-colors cursor-pointer py-2"
                  >
                    {item.label}
                  </Link>
                )
              ))}
              <Button
                onClick={() => {
                  onContactClick();
                  setIsMobileMenuOpen(false);
                }}
                className="text-left"
              >
                {t('common.contact')}
              </Button>
              <LanguageSwitcher isMobile={true} />
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
